import { useCallback, useState } from 'react';
import { CrudService } from '../services/crudFactory';
import { AxiosError } from 'axios';

export interface ValidationErrors {
  [field: string]: string[];
}

export interface UseResourceMutationsResult<T> {
  loading: boolean;
  error: unknown;
  validationErrors: ValidationErrors | null;
  create: (payload: Partial<T>) => Promise<T>;
  update: (id: number | string, payload: Partial<T>) => Promise<T>;
  partialUpdate: (id: number | string, payload: Partial<T>) => Promise<T>;
  remove: (id: number | string) => Promise<void>;
}

export const useResourceMutations = <T>(
  service: CrudService<T>,
): UseResourceMutationsResult<T> => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);

  const mutate = useCallback(async <R>(action: () => Promise<R>): Promise<R> => {
    setLoading(true);
    setError(null);
    setValidationErrors(null);
    try {
      return await action();
    } catch (err) {
      setError(err);
      
      // Extrair erros de validação se existirem
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as AxiosError<any>;
        const responseData = axiosError.response?.data;
        
        console.group('🚨 [useResourceMutations] Erro capturado');
        console.log('Status:', axiosError.response?.status);
        console.log('Response data:', responseData);
        
        // Formato 1: {error: true, details: {...}}
        if (responseData?.details) {
          console.log('✅ Erros de validação encontrados (formato padrão):', responseData.details);
          setValidationErrors(responseData.details);
        }
        // Formato 2: Django REST direto {field: ["error"]}
        else if (responseData && typeof responseData === 'object') {
          const hasArrayValues = Object.values(responseData).some(v => Array.isArray(v));
          if (hasArrayValues) {
            console.log('✅ Erros de validação encontrados (formato DRF):', responseData);
            setValidationErrors(responseData);
          }
        }
        
        console.groupEnd();
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(
    (payload: Partial<T>) => {
      console.group("🆕 [useResourceMutations] CREATE");
      console.log("📦 Payload que será enviado:");
      console.log(JSON.stringify(payload, null, 2));
      console.groupEnd();
      return mutate(() => service.create(payload));
    },
    [mutate, service],
  );

  const update = useCallback(
    (id: number | string, payload: Partial<T>) =>
      mutate(() => service.update(id, payload)),
    [mutate, service],
  );

  const partialUpdate = useCallback(
    (id: number | string, payload: Partial<T>) =>
      mutate(() => service.partialUpdate(id, payload)),
    [mutate, service],
  );

  const remove = useCallback(
    (id: number | string) => mutate(() => service.remove(id)),
    [mutate, service],
  );

  return {
    loading,
    error,
    validationErrors,
    create,
    update,
    partialUpdate,
    remove,
  };
};
